import { useRef } from "react";
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  NavPriceFallbackBackfillDto,
  NavPriceFallbackCustomerDto,
  NavPriceFallbackRefreshResultDto,
  NavPriceFallbackRefreshScope,
  NavPriceFallbackSummaryDto,
} from "../models/responses/NavPriceFallbackDto";
import {
  navPriceFallbackCsvFallbackName,
  resolveCsvFilename,
} from "../../utils/functions/csvFilename";
import { triggerBlobDownload } from "../../utils/functions/blobDownload";

// NAV price fallback (flag REACT_APP_NAV_PRICE_FALLBACK_ADMIN_ENABLED).
// Admin surface with a fixed call budget: the summary is ONE GET on page
// open (5 min stale, no polling, refetch only via the Refresh button), a
// customer's rows are ONE GET the first time the row is expanded (kept for
// 5 min, so collapse/expand costs nothing), the CSV is fetched on click only.
// Both queries run with retry: false - the app's QueryClient sets no retry,
// so react-query's default (3 retries with back-off) would turn a 404
// { disabled: true } or a 5xx into FOUR calls and a ~7 s wait before the
// screen can say what happened. "Fetch NAV prices again" (useRefresh) is
// ONE POST per click, then one summary re-read plus one re-read of the
// customer it ran for (or of every expanded customer for the whole list).
export interface NavPriceFallbackRepository {
  useSummary(options?: { enabled?: boolean }): Resource<NavPriceFallbackSummaryDto>;
  // Idle until `enabled` is true (the row is expanded); never refetches on
  // its own afterwards.
  useCustomer(
    orgId: number | undefined,
    options?: { enabled?: boolean },
  ): Resource<NavPriceFallbackCustomerDto>;
  // The Refresh button: re-reads the summary AND every customer panel that
  // is currently expanded (one call each), and drops the cached rows of the
  // collapsed ones so a later expand reads fresh data instead of a stale
  // count/rows mismatch. Costs nothing for customers that are never expanded.
  refreshAll(): Promise<void>;
  // GET ops/nav-price-fallback/export.csv[?orgId=] -> Blob download.
  // Resolves with the filename used (the server's Content-Disposition name
  // when the browser exposes it, else the same name rebuilt locally).
  downloadCsv(orgId?: number): Promise<string>;
  // "Fetch NAV prices again": one hook instance per button (the header's
  // "all" button, each expanded customer's button). See NavPriceFallbackRefresh.
  useRefresh(): NavPriceFallbackRefresh;
}

export interface NavPriceFallbackRefresh {
  // ONE POST ops/nav-price-fallback/refresh for the scope; resolves with the
  // normalised answer once the summary and the customer (one org) / the
  // expanded customers (all) have been re-read. A call while one is in
  // flight returns the running promise - no second POST, whatever the
  // button state was when the click landed.
  refresh(
    scope: NavPriceFallbackRefreshScope,
  ): Promise<NavPriceFallbackRefreshResultDto>;
  // This hook's own run (the button that was clicked).
  isLoading: boolean;
  // Any refresh run on the screen: the "all" button and every customer
  // button share one mutation key, so every refresh button locks while one
  // runs (the API answers 409/429 to a parallel run anyway).
  isAnyRunning: boolean;
  // The last answer of THIS hook (cleared when a new run starts).
  data: NavPriceFallbackRefreshResultDto | undefined;
  error: unknown;
}

// A counter as the API sent it: a finite number, else 0.
const count = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

// The additive `backfill` block of GET ops/nav-price-fallback/summary (the
// nightly backfill, API flag NavSalesPriceBackfillEnabled) made safe for the
// screen: absent, null or not an object -> undefined (no card, as while the
// API flag is off); otherwise every counter a finite number (missing or odd
// -> 0) and the last-run stamp a non-empty string or null ("never").
export function normalizeNavPriceFallbackBackfill(
  raw: unknown,
): NavPriceFallbackBackfillDto | undefined {
  if (raw === null || raw === undefined || typeof raw !== "object") {
    return undefined;
  }
  const body = raw as Record<string, unknown>;
  return {
    pending: count(body.pending),
    done: count(body.done),
    failed: count(body.failed),
    lastRunUtc:
      typeof body.lastRunUtc === "string" && body.lastRunUtc.trim() !== ""
        ? body.lastRunUtc
        : null,
  };
}

// The summary as the screen reads it: customers and counters untouched, the
// additive `backfill` block normalised (above). A body that is not an object
// is passed through as it is.
export function normalizeNavPriceFallbackSummary(
  raw: NavPriceFallbackSummaryDto,
): NavPriceFallbackSummaryDto {
  if (raw === null || typeof raw !== "object") return raw;
  return { ...raw, backfill: normalizeNavPriceFallbackBackfill(raw.backfill) };
}

// The raw JSON of POST ops/nav-price-fallback/refresh made safe for the
// screen: every counter a finite number (missing or odd -> 0), results an
// array of rows with the four fields present (missing -> "" / null). A body
// that is not an object (null, a string) counts as empty.
export function normalizeNavPriceFallbackRefreshResult(
  raw: unknown,
): NavPriceFallbackRefreshResultDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  const text = (value: unknown): string | null =>
    value === null || value === undefined ? null : String(value);
  const rows = Array.isArray(body.results) ? body.results : [];
  return {
    requested: count(body.requested),
    refreshed: count(body.refreshed),
    unchanged: count(body.unchanged),
    failed: count(body.failed),
    results: rows
      .filter((row) => row !== null && typeof row === "object")
      .map((row) => {
        const r = row as Record<string, unknown>;
        return {
          serial: text(r.serial) ?? "",
          outcome: text(r.outcome) ?? "",
          unitPrice:
            typeof r.unitPrice === "number" && Number.isFinite(r.unitPrice)
              ? r.unitPrice
              : null,
          message: text(r.message),
        };
      }),
  };
}

export const NAV_PRICE_FALLBACK_ROOT_KEY = "navPriceFallback";
export const NAV_PRICE_FALLBACK_REFRESH_KEY = [
  NAV_PRICE_FALLBACK_ROOT_KEY,
  "refresh",
];
const STALE_MS = 5 * 60 * 1000;

export function useNavPriceFallbackRepository(): NavPriceFallbackRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useSummary(options?: {
    enabled?: boolean;
  }): Resource<NavPriceFallbackSummaryDto> {
    return useQuery(
      [NAV_PRICE_FALLBACK_ROOT_KEY, "summary"],
      async () =>
        normalizeNavPriceFallbackSummary(
          await apiClient.getNavPriceFallbackSummary(),
        ),
      {
        staleTime: STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        enabled: options?.enabled ?? true,
      },
    );
  }

  function useCustomer(
    orgId: number | undefined,
    options?: { enabled?: boolean },
  ): Resource<NavPriceFallbackCustomerDto> {
    return useQuery(
      [NAV_PRICE_FALLBACK_ROOT_KEY, "customer", orgId ?? -1],
      () => apiClient.getNavPriceFallbackCustomer(orgId as number),
      {
        staleTime: STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        enabled: orgId !== undefined && (options?.enabled ?? true),
      },
    );
  }

  async function refreshAll(): Promise<void> {
    // Collapsed panels have no observer: their cache entry is removed (no
    // call now, ONE call if expanded again). Mounted panels and the summary
    // are refetched in place by invalidateQueries (refetchType "active").
    queryClient.removeQueries({
      queryKey: [NAV_PRICE_FALLBACK_ROOT_KEY, "customer"],
      type: "inactive",
    });
    await queryClient.invalidateQueries([NAV_PRICE_FALLBACK_ROOT_KEY]);
  }

  function useRefresh(): NavPriceFallbackRefresh {
    const inFlight = useRef<
      Promise<NavPriceFallbackRefreshResultDto> | undefined
    >(undefined);
    const mutation = useMutation<
      NavPriceFallbackRefreshResultDto,
      unknown,
      NavPriceFallbackRefreshScope
    >({
      mutationKey: NAV_PRICE_FALLBACK_REFRESH_KEY,
      mutationFn: async (scope) =>
        normalizeNavPriceFallbackRefreshResult(
          await apiClient.refreshNavPriceFallback(scope),
        ),
      // Awaited by mutateAsync: the button stays "running" until the list
      // shows the re-read numbers. A failed re-read is not a failed refresh
      // (invalidateQueries never throws) - the screen's own error banner
      // says it.
      onSuccess: async (_result, scope) => {
        if ("all" in scope) {
          await refreshAll();
          return;
        }
        await Promise.all([
          queryClient.invalidateQueries([NAV_PRICE_FALLBACK_ROOT_KEY, "summary"]),
          queryClient.invalidateQueries([
            NAV_PRICE_FALLBACK_ROOT_KEY,
            "customer",
            scope.orgId,
          ]),
        ]);
      },
    });
    const isAnyRunning =
      useIsMutating({ mutationKey: NAV_PRICE_FALLBACK_REFRESH_KEY }) > 0;

    function refresh(
      scope: NavPriceFallbackRefreshScope,
    ): Promise<NavPriceFallbackRefreshResultDto> {
      // The ref is set synchronously: react-query flips isLoading only after
      // a microtask, so two clicks in the same tick would otherwise both
      // pass a state-based guard.
      if (inFlight.current) return inFlight.current;
      const run = mutation.mutateAsync(scope).finally(() => {
        inFlight.current = undefined;
      });
      inFlight.current = run;
      return run;
    }

    return {
      refresh,
      isLoading: mutation.isLoading,
      isAnyRunning,
      data: mutation.data,
      error: mutation.error,
    };
  }

  async function downloadCsv(orgId?: number): Promise<string> {
    const response = await apiClient.exportNavPriceFallbackCsv(orgId);
    const blob = await response.blob();
    const filename = resolveCsvFilename(
      response.headers.get("Content-Disposition"),
      navPriceFallbackCsvFallbackName(orgId),
    );
    triggerBlobDownload(blob, filename);
    return filename;
  }

  return {
    useSummary,
    useCustomer,
    refreshAll,
    downloadCsv,
    useRefresh,
  };
}
